import { formatPaise } from "@luckymenu/core";
import { useTranslation } from "@luckymenu/i18n/react";
import type { BillCalculation, BillLine } from "@luckymenu/staff-core";
import {
	type BillDiscount,
	type BillingError,
	billLineName,
	billRows,
	type DiscountDraft,
	discountFromDraft,
	discountToDraft,
	EMPTY_DISCOUNT_DRAFT,
	lineDiscountWhy,
	taxIncluded,
} from "@luckymenu/staff-core";
import {
	Button,
	cn,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Textarea,
} from "@luckymenu/ui-web";
import { BadgePercent } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";

/** A billing refusal in words, with the amount or limit it carries. */
export function useProblemText() {
	const { t } = useTranslation("billing");
	return (problem: BillingError | null): string | null => {
		if (!problem) return null;
		if (problem.problem === "totalChanged" && problem.totalPaise !== undefined)
			return t("problems.totalChangedTo", { total: formatPaise(problem.totalPaise) });
		if (problem.problem === "moreThanBalance" && problem.balancePaise !== undefined)
			return t("problems.moreThanBalanceOf", { balance: formatPaise(problem.balancePaise) });
		return t(`problems.${problem.problem}`);
	};
}

/** A refusal under the action that caused it. */
export function ProblemText({ problem }: { problem: BillingError | null }) {
	const text = useProblemText()(problem);
	if (!text) return null;
	return (
		<p role="alert" className="text-sm text-destructive">
			{text}
		</p>
	);
}

/** The bill's lines: quantity, name, add-ons, amount, and an item's discount under it. */
export function BillLines({
	calculation,
	action,
}: {
	calculation: BillCalculation;
	/** Beside each line, such as its discount button. */
	action?: (line: BillLine) => ReactNode;
}) {
	const { t } = useTranslation("billing");
	return (
		<ul aria-label={t("billScreen.items")} className="divide-y text-sm">
			{calculation.lines.map((line) => (
				<li key={line.id} className="flex items-start gap-2 py-2">
					<span className="w-8 shrink-0 font-semibold tabular-nums">{line.quantity}×</span>
					<span className="grid min-w-0 flex-1 gap-0.5">
						<span>{billLineName(line)}</span>
						{line.modifiers.length > 0 ? (
							<span className="text-xs text-muted-foreground">
								{line.modifiers.map((m) => m.name).join(", ")}
							</span>
						) : null}
						{line.itemDiscountPaise > 0 ? (
							<span className="text-xs text-primary">
								{lineDiscountWhy(line)
									? t("billScreen.discountBecause", {
											amount: formatPaise(line.itemDiscountPaise),
											reason: lineDiscountWhy(line),
										})
									: t("billScreen.discountOff", { amount: formatPaise(line.itemDiscountPaise) })}
							</span>
						) : null}
					</span>
					<span className="shrink-0 tabular-nums">{formatPaise(line.grossPaise)}</span>
					{action?.(line)}
				</li>
			))}
		</ul>
	);
}

/**
 * Item total, discounts, service charge, CGST and SGST by rate, round-off
 * and the total, large. Every amount is billing-core's.
 */
export function BillTotals({
	calculation,
	className,
}: {
	calculation: BillCalculation;
	className?: string;
}) {
	const { t } = useTranslation("billing");
	return (
		<div className={cn("grid gap-1", className)}>
			<dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
				{billRows(calculation).map((row) => (
					<div key={row.key} className="contents">
						<dt className={cn(row.key !== "items" && "text-muted-foreground")}>
							{row.label ?? t(`rows.${row.labelKey}`, { rate: row.rate ?? "" })}
						</dt>
						<dd
							className={cn(
								"text-right tabular-nums",
								row.key !== "items" && "text-muted-foreground",
								row.paise < 0 && row.key !== "roundOff" && "text-primary",
							)}
						>
							{formatPaise(row.paise)}
						</dd>
					</div>
				))}
				<dt className="pt-1 text-lg font-semibold">{t("billScreen.total")}</dt>
				<dd className="pt-1 text-right text-lg font-semibold tabular-nums">
					{formatPaise(calculation.totals.totalPaise)}
				</dd>
			</dl>
			{taxIncluded(calculation) ? (
				<p className="text-xs text-muted-foreground">{t("billScreen.pricesIncludeTax")}</p>
			) : null}
		</div>
	);
}

/** The small button beside a line that opens its discount. */
export function LineDiscountButton({ line, onClick }: { line: BillLine; onClick: () => void }) {
	const { t } = useTranslation("billing");
	return (
		<Button
			variant="ghost"
			size="icon"
			aria-label={t("billScreen.discountOnItem", { item: billLineName(line) })}
			aria-pressed={line.discount !== null}
			onClick={onClick}
			className={cn("-my-2", line.discount && "text-primary")}
		>
			<BadgePercent />
		</Button>
	);
}

/**
 * Percent or amount, how much, and a reason (BL-07): common reasons in one
 * tap, or typed. Shows what it comes to before Apply. Remove takes off a
 * discount given before.
 */
export function DiscountDialog({
	title,
	initial,
	amountOf,
	onApply,
	onRemove,
	onClose,
}: {
	title: string;
	initial: BillDiscount | null;
	/** What a discount takes off, by billing-core, for the "Comes to" line. */
	amountOf: (discount: BillDiscount) => number;
	onApply: (discount: BillDiscount) => void;
	onRemove: () => void;
	onClose: () => void;
}) {
	const { t } = useTranslation("billing");
	const valueId = useId();
	const reasonId = useId();
	const [draft, setDraft] = useState<DiscountDraft>(() =>
		initial ? discountToDraft(initial) : EMPTY_DISCOUNT_DRAFT,
	);
	const [submitted, setSubmitted] = useState(false);
	const read = discountFromDraft(draft);
	const problems = submitted && !read.ok ? read.problems : {};
	const set = (patch: Partial<DiscountDraft>) => setDraft((d) => ({ ...d, ...patch }));
	const submit = (event: FormEvent) => {
		event.preventDefault();
		setSubmitted(true);
		if (read.ok) onApply(read.discount);
	};
	const reasons = (["regular", "complaint", "staff", "owner"] as const).map((k) =>
		t(`discount.reasons.${k}`),
	);
	// Before a reason is given, the amount alone already shows what it comes to.
	const valueOnly = discountFromDraft({ ...draft, reason: draft.reason || "-" });
	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-md">
				<form onSubmit={submit} className="grid gap-4" noValidate>
					<DialogHeader>
						<DialogTitle>{title}</DialogTitle>
						<DialogDescription>{t("discount.body")}</DialogDescription>
					</DialogHeader>
					<fieldset className="grid gap-1.5">
						<legend className="mb-1.5 text-sm font-medium">{t("discount.type")}</legend>
						<div className="grid grid-cols-2 gap-2">
							{(["percent", "amount"] as const).map((type) => (
								<Button
									key={type}
									type="button"
									variant={draft.type === type ? "default" : "outline"}
									aria-pressed={draft.type === type}
									onClick={() => set({ type })}
								>
									{t(`discount.${type}`)}
								</Button>
							))}
						</div>
					</fieldset>
					<div className="grid gap-1.5">
						<Label htmlFor={valueId}>
							{draft.type === "percent" ? t("discount.valuePercent") : t("discount.valueAmount")}
						</Label>
						<div className="relative">
							<Input
								id={valueId}
								autoFocus
								inputMode="decimal"
								autoComplete="off"
								value={draft.value}
								aria-invalid={problems.value ? true : undefined}
								onChange={(e) => set({ value: e.target.value })}
								className={cn("tabular-nums", draft.type === "amount" ? "pl-7" : "pr-8")}
							/>
							<span
								aria-hidden
								className={cn(
									"pointer-events-none absolute top-1/2 -translate-y-1/2 text-sm text-muted-foreground",
									draft.type === "amount" ? "left-3" : "right-3",
								)}
							>
								{draft.type === "amount" ? "₹" : "%"}
							</span>
						</div>
						{problems.value ? (
							<p role="alert" className="text-sm text-destructive">
								{t(`discount.problems.${problems.value}`)}
							</p>
						) : valueOnly.ok ? (
							<p className="text-sm text-muted-foreground tabular-nums">
								{t("discount.comesTo", { amount: formatPaise(amountOf(valueOnly.discount)) })}
							</p>
						) : null}
					</div>
					<div className="grid gap-1.5">
						<Label htmlFor={reasonId}>{t("discount.reason")}</Label>
						<div className="flex flex-wrap gap-2">
							{reasons.map((text) => (
								<button
									key={text}
									type="button"
									aria-pressed={draft.reason === text}
									onClick={() => set({ reason: text })}
									className={cn(
										"min-h-10 rounded-full border px-3 text-sm hover:bg-muted pointer-coarse:min-h-12",
										draft.reason === text && "border-primary bg-primary/10",
									)}
								>
									{text}
								</button>
							))}
						</div>
						<Textarea
							id={reasonId}
							value={draft.reason}
							maxLength={200}
							rows={2}
							aria-invalid={problems.reason ? true : undefined}
							onChange={(e) => set({ reason: e.target.value })}
						/>
						{problems.reason ? (
							<p role="alert" className="text-sm text-destructive">
								{t(`discount.problems.${problems.reason}`)}
							</p>
						) : null}
					</div>
					<DialogFooter className="sm:justify-between">
						{initial ? (
							<Button type="button" variant="ghost" className="text-destructive" onClick={onRemove}>
								{t("discount.remove")}
							</Button>
						) : (
							<span />
						)}
						<span className="flex gap-2">
							<Button type="button" variant="outline" onClick={onClose}>
								{t("discount.cancel")}
							</Button>
							<Button type="submit">{t("discount.apply")}</Button>
						</span>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
